import { bindingFromEvent, effectiveShortcuts, isEditableTarget, type ShortcutAction } from './shortcuts';
import { DEFAULT_TOOLS } from '../types/config';
import { t, uiDir } from '../i18n';
/**
 * Toolbar - Horizontal sticky accessibility bar (Read&Write style)
 * Replaces FloatingPlayer for Page/Highlight modes. 48px height, fixed top or bottom.
 */

import type { ResolvedTheme } from './ThemeManager';
import { contrastRatio, hexToRgb, resolveTheme } from './ThemeManager';
import type { VoiceId, ToolId, ToolbarTheme } from '../types/config';
import type { ToolManager } from '../tools/ToolManager';
import type { WidgetFeatureConfig } from '../types/api';
import { loadExtras } from '../lazy';
import { ICONS } from './icons';
import { ensureFocusStyles } from './a11y';

/** Tools that open a dropdown panel (aria-expanded) rather than toggling a mode (aria-pressed) */
const PANEL_TOOLS = new Set<string>(['pageTheme', 'fontSize', 'fontSelector', 'settings']);

export interface ToolbarCallbacks {
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSpeedChange: (speed: number) => void;
  onVoiceChange: (voice: string) => void;
  onDictionary?: (word: string) => void;
  onTranslate?: (text: string) => void;
  /** Download the page (or selection) as MP3; resolves with a message for the visitor */
  onDownload?: (onProgress: (done: number, total: number) => void) => Promise<{ ok: boolean; message: string }>;
  /** Full-page "Listen in <language>" — 'en' = original */
  onLanguageChange?: (code: string) => void;
  /** Called once, the first time the toolbar is shown (lazy tool loading) */
  onFirstShow?: () => void;
  /** Jump sentences during playback (+1 next, −1 previous) — buttons and keyboard shortcuts */
  onSkip?: (delta: number) => void;
  /** AI summary of the page/selection */
  onSummarize?: () => Promise<{ ok: boolean; bullets?: string[]; message?: string }>;
  /** Read the summary aloud */
  onSpeakSummary?: (bullets: string[]) => void;
  /** Seek bar: jump to a position (0–100) */
  onSeek?: (percent: number) => void;
  /** Seek bar: current position (0–100) */
  getProgress?: () => number;
  onClose: () => void;
}

const SPEED_STEPS = [0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 1.8, 1.9, 2.0];

// Voice selection moved to SettingsPanel


// ReadingVox waveform logo (compact)
const LOGO_SVG = `<svg viewBox="26 57 167 109" width="22" height="22" xmlns="http://www.w3.org/2000/svg"> <rect x="31" y="90" width="26" height="45" rx="13" fill="#2ec486"/> <rect x="63" y="65" width="26" height="95" rx="13" fill="#8d99ae"/> <rect x="96" y="82" width="26" height="61" rx="13" fill="#2ec486"/> <rect x="129" y="65" width="26" height="95" rx="13" fill="#ff9f1c"/> <rect x="161" y="90" width="26" height="45" rx="13" fill="#8d99ae"/> </svg>`;

export class Toolbar {
  private element: HTMLDivElement;
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;
  private callbacks: ToolbarCallbacks;
  private toolManager: ToolManager;
  private theme: ResolvedTheme;
  private position: 'top' | 'bottom';
  private ttsMode: string;
  private isPlaying: boolean = false;
  private currentSpeedIndex: number = 5; // 1.0x
  private enabledTools: ToolId[];
  private dropdownElements: Map<ToolId, HTMLDivElement> = new Map();
  private baseThemeConfig: ResolvedTheme;
  private hideVoiceSelector: boolean;
  private features: WidgetFeatureConfig | null = null;
  private language: string = 'en';
  private captionEl: HTMLDivElement | null = null;
  private boundDocMouseDown: (e: MouseEvent) => void;
  private ariaObserver: MutationObserver | null = null;

  constructor(options: {
    callbacks: ToolbarCallbacks;
    toolManager: ToolManager;
    theme: ResolvedTheme;
    position?: 'top' | 'bottom';
    enabledTools?: ToolId[];
    ttsMode?: string;
    hideVoiceSelector?: boolean;
    /** Leave this many px free on the left (fixed side nav, e.g. Canvas) on viewports ≥768px */
    insetLeft?: number;
    /** Leave this many px free on the right (fixed corner button, e.g. Moodle's "?") on viewports ≥768px */
    insetRight?: number;
    /** Page-level keyboard shortcuts (default true; the extension uses Chrome commands) */
    keyboardShortcuts?: boolean;
  }) {
    this.callbacks = options.callbacks;
    this.toolManager = options.toolManager;
    this.theme = options.theme;
    this.baseThemeConfig = options.theme;
    this.position = options.position || 'top';
    this.ttsMode = options.ttsMode || 'hybrid';
    this.hideVoiceSelector = options.hideVoiceSelector || false;
    this.enabledTools = options.enabledTools || DEFAULT_TOOLS;
    this.boundDocMouseDown = (e: MouseEvent) => this.handleDocumentMouseDown(e);
    this.element = this.createElement();
    if (options.insetLeft && options.insetLeft > 0) this.applyInset('left', Math.round(options.insetLeft));
    if (options.insetRight && options.insetRight > 0) this.applyInset('right', Math.round(options.insetRight));
    document.addEventListener('mousedown', this.boundDocMouseDown);
    if (options.keyboardShortcuts !== false) {
      this.boundKeyDown = (e: KeyboardEvent) => this.handleShortcutKey(e);
      document.addEventListener('keydown', this.boundKeyDown, true);
    }

    // Accessible names / states for the icon buttons, kept in sync with the
    // tooltip text and the `rv-active` class however they change
    ensureFocusStyles();
    this.syncAria();
    if (typeof MutationObserver !== 'undefined') {
      this.ariaObserver = new MutationObserver(() => this.syncAria());
      this.ariaObserver.observe(this.element, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'data-tooltip'] });
    }

    // Listen for settings changes (e.g. toolbar theme toggle)
    this.toolManager.onSettingsChanged((settings) => {
      if (settings.toolbarTheme) {
        this.refreshTheme(settings.toolbarTheme);
      }
    });
  }

  private applyInset(side: 'left' | 'right', px: number): void {
    const id = side === 'left' ? `rv-toolbar-inset-${px}` : `rv-toolbar-inset-r-${px}`;
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = `@media (min-width: 768px) { .rv-toolbar.${id} { ${side}: ${px}px !important; } }`;
      document.head.appendChild(style);
    }
    this.element.classList.add(id);
  }

  private isToolEnabled(id: ToolId): boolean {
    return this.enabledTools.includes(id);
  }

  private createElement(): HTMLDivElement {
    const bar = document.createElement('div');
    bar.className = 'rv-toolbar';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', t('ReadingVox reading toolbar'));

    const th = this.theme;
    const positionCSS = this.position === 'top' ? 'top: 0;' : 'bottom: 0;';

    bar.style.cssText = ` position: fixed; ${positionCSS} left: 0; right: 0; height: 48px; background: ${th.backgroundColor}; border-${this.position === 'top' ? 'bottom' : 'top'}: 1px solid ${th.borderColor}; box-shadow: 0 2px 8px rgba(0,0,0,0.1); z-index: 2147483647; display: none; align-items: center; padding: 0 12px; gap: 4px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: ${th.textColor}; `;

    // Inject styles
    this.injectStyles();

    // Build toolbar content
    bar.innerHTML = this.buildHTML();
    if (this.callbacks.getProgress && this.callbacks.onSeek) bar.appendChild(this.createSeekBar());
    document.body.appendChild(bar);

    this.attachEventListeners(bar);

    return bar;
  }

  private buildHTML(): string {
    const th = this.theme;
    const parts: string[] = [];

    // Logo
    parts.push(`<a href="https://readingvox.com/support" target="_blank" rel="noopener" class="rv-tb-logo" style="display:flex;align-items:center;padding:0 6px;flex-shrink:0;text-decoration:none;" title="${t('ReadingVox Support')}">${LOGO_SVG}</a>`);
    parts.push(this.divider());

    // === Playback group ===
    parts.push(` <button class="rv-tb-btn rv-tb-play" data-tooltip="${t('Play')}" style="background:${th.primaryColor};color:${th.textOnPrimary};border-radius:6px;"> <span class="rv-tb-play-icon">${ICONS.play}</span> <span class="rv-tb-pause-icon" style="display:none;">${ICONS.pause}</span> </button> <button class="rv-tb-btn rv-tb-stop" data-tooltip="${t('Stop')}">${ICONS.stop}</button> ${this.callbacks.onSkip ? ` <button class="rv-tb-btn rv-tb-prev" data-tooltip="${t('Previous sentence')}">${ICONS.prev}</button> <button class="rv-tb-btn rv-tb-next" data-tooltip="${t('Next sentence')}">${ICONS.next}</button>` : ''} `);

    parts.push(this.divider());

    // Speed controls
    parts.push(` <button class="rv-tb-btn rv-tb-speed-down" data-tooltip="${t('Slower')}">${ICONS.speedDown}</button> <span class="rv-tb-speed-label" style="font-size:12px;font-weight:600;color:${th.primaryColor};min-width:32px;text-align:center;">1x</span> <button class="rv-tb-btn rv-tb-speed-up" data-tooltip="${t('Faster')}">${ICONS.speedUp}</button> `);

    // "Listen in <language>" selector — filled by setFeatures() when fullPageTranslation is on
    parts.push(`<span class="rv-tb-lang-slot" style="display:contents;">${this.buildLanguageHTML()}</span>`);

    parts.push(this.divider());

    // === Reading tools group ===
    if (this.isToolEnabled('simplify')) {
      parts.push(this.toolBtn('simplify', t('Simplify')));
    }
    if (this.isToolEnabled('vocabSimplify')) {
      parts.push(this.toolBtn('vocabSimplify', t('Simplify Vocabulary')));
    }
    if (this.isToolEnabled('dictionary') && this.callbacks.onDictionary) {
      parts.push(this.toolBtn('dictionary', t('Dictionary')));
    }
    if (this.isToolEnabled('translate') && this.callbacks.onTranslate) {
      // Hidden until /widget/config confirms selection translation (see setFeatures)
      parts.push(this.toolBtn('translate', t('Translate')).replace('<button ', `<button style="display:${this.features?.selectionTranslation ? 'inline-flex' : 'none'};" `));
    }
    if (this.isToolEnabled('summary') && this.callbacks.onSummarize) {
      parts.push(this.toolBtn('summary', t('Summarize')));
    }
    if (this.isToolEnabled('clickListen')) {
      parts.push(this.toolBtn('clickListen', t('Click and listen')));
    }
    if (this.isToolEnabled('formReader')) {
      parts.push(this.toolBtn('formReader', t('Form reader')));
    }
    if (this.isToolEnabled('download') && this.callbacks.onDownload) {
      parts.push(this.toolBtn('download', t('Download MP3')));
    }
    if (this.isToolEnabled('magnifier')) {
      parts.push(this.toolBtn('magnifier', t('Magnifier')));
    }
    if (this.isToolEnabled('screenshotReader')) {
      parts.push(this.toolBtn('screenshotReader', t('Screenshot Reader')));
    }

    parts.push(this.divider());

    // === Display tools group ===
    if (this.isToolEnabled('pageTheme')) {
      parts.push(this.toolBtn('pageTheme', t('Theme')));
    }
    if (this.isToolEnabled('fontSize')) {
      parts.push(this.toolBtn('fontSize', t('Font Size')));
    }
    if (this.isToolEnabled('fontSelector')) {
      parts.push(this.toolBtn('fontSelector', t('Font')));
    }
    if (this.isToolEnabled('screenMask')) {
      parts.push(this.toolBtn('screenMask', t('Mask')));
    }

    parts.push(this.divider());

    // === Settings & close ===
    if (this.isToolEnabled('settings')) {
      parts.push(this.toolBtn('settings', t('Settings')));
    }

    // Help button
    parts.push(`<button class="rv-tb-btn rv-tb-help" data-tooltip="${t('Help')}">${ICONS.help}</button>`);

    // "Powered by ReadingVox" (plan-driven, see setFeatures)
    parts.push(`<span class="rv-tb-brand-slot" style="display:contents;">${this.buildBrandingHTML()}</span>`);

    parts.push(`<button class="rv-tb-btn rv-tb-close" data-tooltip="${t('Close')}" style="${this.features?.branding ? '' : 'margin-left:auto;'}">${ICONS.close}</button>`);

    return parts.join('');
  }

  private buildLanguageHTML(): string {
    const f = this.features;
    if (!f?.fullPageTranslation || f.languages.length === 0 || !this.callbacks.onLanguageChange) return '';
    const th = this.theme;
    const esc = (v: string) => v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const options = [`<option value="en"${this.language === 'en' ? ' selected' : ''}>English</option>`]
      .concat(f.languages.filter(l => l.code !== 'en').map(l =>
        `<option value="${esc(l.code)}"${l.code === this.language ? ' selected' : ''}>${esc(l.name)}</option>`
      ))
      .join('');
    return ` ${this.divider()} <select class="rv-tb-lang" aria-label="${t('Listen in language')}" title="${t('Listen in language')}" style=" height:30px;max-width:130px;font-size:12px;padding:0 6px;border:1px solid ${th.borderColor}; border-radius:6px;background:${th.backgroundColor};color:${th.textColor};cursor:pointer;flex-shrink:0; ">${options}</select> <span class="rv-tb-mt rv-muted" style="font-size:11px;margin-left:6px;white-space:nowrap;flex-shrink:0;display:${this.language === 'en' ? 'none' : 'inline'};">${t('Machine translated')}</span> `;
  }

  private buildBrandingHTML(): string {
    if (!this.features?.branding) return '';
    return `<a class="rv-tb-powered rv-muted" href="https://readingvox.com/?ref=widget" target="_blank" rel="noopener" style="margin-left:auto;margin-right:6px;font-size:11px;text-decoration:none;white-space:nowrap;flex-shrink:0;">${t('Powered by ReadingVox')}</a>`;
  }

  /**
   * Apply server feature flags (translation, branding, language list)
   */
  /** Server feature flags, once /widget/config has answered (null before) */
  getFeatures(): WidgetFeatureConfig | null {
    return this.features;
  }

  setFeatures(features: WidgetFeatureConfig): void {
    this.features = features;
    if (!features.fullPageTranslation && this.language !== 'en') {
      this.language = 'en';
    }
    this.applyFeatureSlots();
  }

  private applyFeatureSlots(): void {
    const translateBtn = this.getToolButton('translate');
    if (translateBtn) translateBtn.style.display = this.features?.selectionTranslation ? 'inline-flex' : 'none';

    const langSlot = this.element.querySelector('.rv-tb-lang-slot') as HTMLElement | null;
    if (langSlot) {
      langSlot.innerHTML = this.buildLanguageHTML();
      const select = langSlot.querySelector('.rv-tb-lang') as HTMLSelectElement | null;
      select?.addEventListener('change', () => {
        this.language = select.value;
        const label = langSlot.querySelector('.rv-tb-mt') as HTMLElement | null;
        if (label) label.style.display = this.language === 'en' ? 'none' : 'inline';
        if (this.language === 'en') this.setCaption(null);
        this.setState({ isPlaying: false });
        this.callbacks.onLanguageChange?.(this.language);
      });
    }

    const brandSlot = this.element.querySelector('.rv-tb-brand-slot') as HTMLElement | null;
    if (brandSlot) brandSlot.innerHTML = this.buildBrandingHTML();
    const closeBtn = this.element.querySelector('.rv-tb-close') as HTMLElement | null;
    if (closeBtn) closeBtn.style.marginLeft = this.features?.branding ? '' : 'auto';
  }

  /**
   * Show the sentence currently being read in translation (null = hide).
   * Translated playback doesn't highlight the on-page (English) text.
   */
  setCaption(text: string | null): void {
    if (!text) {
      if (this.captionEl) this.captionEl.style.display = 'none';
      return;
    }
    if (!this.captionEl) {
      const th = this.theme;
      const el = document.createElement('div');
      el.className = 'rv-tb-caption';
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = ` position: fixed; ${this.position === 'top' ? 'top: 52px;' : 'bottom: 52px;'} left: 50%; transform: translateX(-50%); max-width: min(720px, calc(100vw - 32px)); background: ${th.backgroundColor}; color: ${th.textColor}; border: 1px solid ${th.borderColor}; border-radius: 10px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); padding: 10px 14px; font: 16px/1.45 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; z-index: 2147483646; display: none; `;
      document.body.appendChild(el);
      this.captionEl = el;
    }
    this.captionEl.textContent = text;
    this.captionEl.style.display = this.isVisible() ? 'block' : 'none';
  }

  /**
   * aria-label from the tooltip; aria-pressed for on/off tools (simplify, mask,
   * magnifier…); aria-expanded + aria-haspopup for tools that open a panel
   * (theme, font, settings, help). One-shot actions (dictionary, translate) get a label only.
   */
  private syncAria(): void {
    this.element.querySelectorAll<HTMLElement>('.rv-tb-btn').forEach((btn) => {
      const tip = btn.getAttribute('data-tooltip');
      if (tip && btn.getAttribute('aria-label') !== tip) btn.setAttribute('aria-label', tip);
      const active = btn.classList.contains('rv-active');
      let kind: 'pressed' | 'expanded' | null = null;
      if (btn.classList.contains('rv-tb-help')) {
        kind = 'expanded';
      } else {
        const id = btn.getAttribute('data-tool') as ToolId | null;
        if (id && id !== 'dictionary' && id !== 'translate') {
          // Tools may register after the toolbar is built — fall back to known panel tools
          const tool = this.toolManager.getTool(id);
          const panel = tool ? !tool.isOverlay : PANEL_TOOLS.has(id);
          kind = panel ? 'expanded' : 'pressed';
        }
      }
      if (kind === 'expanded') {
        if (btn.getAttribute('aria-haspopup') !== 'true') btn.setAttribute('aria-haspopup', 'true');
        if (btn.getAttribute('aria-expanded') !== String(active)) btn.setAttribute('aria-expanded', String(active));
      } else if (kind === 'pressed') {
        if (btn.getAttribute('aria-pressed') !== String(active)) btn.setAttribute('aria-pressed', String(active));
      }
    });
  }

  private toolBtn(id: ToolId, tooltip: string): string {
    return `<button class="rv-tb-btn rv-tb-tool" data-tool="${id}" data-tooltip="${tooltip}">${ICONS[id] || ''}</button>`;
  }

  private divider(): string {
    return `<div class="rv-tb-divider" style="width:1px;height:24px;background:${this.theme.borderColor};margin:0 4px;flex-shrink:0;"></div>`;
  }

  private injectStyles(): void {
    const styleId = 'rv-toolbar-styles';
    let style = document.getElementById(styleId) as HTMLStyleElement;
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    const th = this.theme;
    // Muted text: start at midpoint between text and bg, shift toward text until WCAG AA 4.5:1
    const mutedColor = this.computeWcagMuted(th.textColor, th.backgroundColor);

    style.textContent = ` :root { --rv-text: ${th.textColor}; --rv-bg: ${th.backgroundColor}; --rv-border: ${th.borderColor}; --rv-primary: ${th.primaryColor}; --rv-primary-on: ${th.textOnPrimary}; --rv-secondary: ${th.secondaryColor}; --rv-muted: ${mutedColor}; } .rv-tb-btn { width: 36px; height: 36px; border: none; background: transparent; cursor: pointer; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; color: ${th.textColor}; transition: all 0.15s; position: relative; flex-shrink: 0; padding: 0; } .rv-tb-btn:hover { background: ${th.secondaryColor}; } .rv-tb-btn.rv-active { background: ${th.primaryColor}20; color: ${th.primaryColor}; box-shadow: inset 0 0 0 2px ${th.primaryColor}40; } .rv-tb-btn[data-tooltip]:hover::after { content: attr(data-tooltip); position: absolute; ${this.position === 'top' ? 'top: 100%; margin-top: 6px;' : 'bottom: 100%; margin-bottom: 6px;'} left: 50%; transform: translateX(-50%); background: #333; color: #fff; padding: 4px 8px; border-radius: 4px; font-size: 11px; white-space: nowrap; z-index: 2147483647; pointer-events: none; } .rv-tb-badge { position: absolute; top: 2px; right: 2px; background: ${th.primaryColor}; color: ${th.textOnPrimary}; font-size: 9px; font-weight: 700; padding: 1px 3px; border-radius: 3px; line-height: 1; pointer-events: none; } .rv-tb-dropdown { position: fixed; ${this.position === 'top' ? 'top: 48px;' : 'bottom: 48px;'} background: ${th.backgroundColor}; border: 1px solid ${th.borderColor}; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.15); padding: 12px; z-index: 2147483647; min-width: 200px; max-height: calc(100vh - 80px); overflow-y: auto; display: none; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: ${th.textColor}; } .rv-tb-dropdown.rv-open { display: block; } .rv-muted { color: var(--rv-muted) !important; } `;
  }

  private attachEventListeners(bar: HTMLDivElement): void {
    // Play/Pause
    const playBtn = bar.querySelector('.rv-tb-play') as HTMLButtonElement;
    playBtn?.addEventListener('click', () => {
      if (this.isPlaying) {
        this.callbacks.onPause();
        this.setState({ isPlaying: false });
      } else {
        this.callbacks.onPlay();
        this.setState({ isPlaying: true });
      }
    });

    // Stop
    const stopBtn = bar.querySelector('.rv-tb-stop') as HTMLButtonElement;
    stopBtn?.addEventListener('click', () => {
      this.callbacks.onStop();
      this.setState({ isPlaying: false });
    });

    // Previous / next sentence
    (bar.querySelector('.rv-tb-prev') as HTMLButtonElement | null)?.addEventListener('click', () => {
      this.callbacks.onSkip?.(-1);
    });
    (bar.querySelector('.rv-tb-next') as HTMLButtonElement | null)?.addEventListener('click', () => {
      this.callbacks.onSkip?.(1);
    });

    // Speed
    const speedDown = bar.querySelector('.rv-tb-speed-down') as HTMLButtonElement;
    const speedUp = bar.querySelector('.rv-tb-speed-up') as HTMLButtonElement;
    const speedLabel = bar.querySelector('.rv-tb-speed-label') as HTMLSpanElement;

    speedDown?.addEventListener('click', () => {
      if (this.currentSpeedIndex > 0) {
        this.currentSpeedIndex--;
        const speed = SPEED_STEPS[this.currentSpeedIndex];
        speedLabel.textContent = speed === 1.0 ? '1x' : `${speed}x`;
        this.callbacks.onSpeedChange(speed);
      }
    });

    speedUp?.addEventListener('click', () => {
      if (this.currentSpeedIndex < SPEED_STEPS.length - 1) {
        this.currentSpeedIndex++;
        const speed = SPEED_STEPS[this.currentSpeedIndex];
        speedLabel.textContent = speed === 1.0 ? '1x' : `${speed}x`;
        this.callbacks.onSpeedChange(speed);
      }
    });

    // Voice changes come from SettingsPanel via ToolManager

    // Tool buttons
    const toolBtns = bar.querySelectorAll('.rv-tb-tool') as NodeListOf<HTMLButtonElement>;
    toolBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const toolId = btn.getAttribute('data-tool') as ToolId;
        if (!toolId) return;

        // Dictionary and Translate are action buttons, not toggleable tools
        if (toolId === 'dictionary' && this.callbacks.onDictionary) {
          this.toolManager.trackToolAction('dictionary');
          this.flashButton(btn);
          const sel = window.getSelection()?.toString().trim();
          if (sel) {
            const word = sel.split(/\s+/)[0].replace(/[^\w'-]/g, '');
            if (word) { this.callbacks.onDictionary(word); return; }
          }
          this.showWordInput(t('Look up a word'), (word) => {
            if (word) this.callbacks.onDictionary!(word);
          });
          return;
        }

        if (toolId === 'translate' && this.callbacks.onTranslate) {
          this.toolManager.trackToolAction('translate');
          this.flashButton(btn);
          const sel = window.getSelection()?.toString().trim();
          if (sel) { this.callbacks.onTranslate(sel); return; }
          this.showWordInput(t('Type text to translate'), (text) => {
            if (text) this.callbacks.onTranslate!(text);
          });
          return;
        }

        if (toolId === 'summary' && this.callbacks.onSummarize) {
          this.toolManager.trackToolAction('summary');
          this.flashButton(btn);
          this.showSummaryPanel();
          return;
        }

        if (toolId === 'download' && this.callbacks.onDownload) {
          if (btn.getAttribute('aria-busy') === 'true') return;
          this.toolManager.trackToolAction('download');
          const label = t('Download MP3');
          const setLabel = (text: string) => {
            btn.setAttribute('data-tooltip', text);
            btn.setAttribute('aria-label', text);
          };
          btn.setAttribute('aria-busy', 'true');
          btn.style.opacity = '0.6';
          setLabel(t('Preparing audio…'));
          this.callbacks.onDownload((done, total) => setLabel(t('Preparing audio… {done}/{total}', { done, total })))
            .then((r) => setLabel(r.message))
            .catch(() => setLabel(t("Couldn't prepare the audio")))
            .finally(() => {
              btn.removeAttribute('aria-busy');
              btn.style.opacity = '';
              setTimeout(() => setLabel(label), 4000);
            });
          return;
        }

        // Toggle tool
        this.toolManager.toggleTool(toolId);

        // Update button active state
        const tool = this.toolManager.getTool(toolId);
        if (tool) {
          btn.classList.toggle('rv-active', tool.isActive);
        }
      });
    });

    // Help
    const helpBtn = bar.querySelector('.rv-tb-help') as HTMLButtonElement;
    helpBtn?.addEventListener('click', () => {
      this.toggleHelpDropdown();
    });

    // Close
    const closeBtn = bar.querySelector('.rv-tb-close') as HTMLButtonElement;
    closeBtn?.addEventListener('click', () => {
      this.callbacks.onClose();
    });

  }

  /**
   * Close dropdowns when clicking outside (registered once in the constructor)
   */
  private handleDocumentMouseDown(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    if (!target?.closest) return;
    if (!target.closest('.rv-toolbar') && !target.closest('.rv-tb-dropdown')) {
      this.toolManager.closeAllTools();
      this.updateToolButtonStates();
      // Also close help dropdown
      if (this.helpOpen) {
        const helpDD = this.dropdownElements.get('help' as any);
        helpDD?.classList.remove('rv-open');
        this.helpOpen = false;
        this.element.querySelector('.rv-tb-help')?.classList.remove('rv-active');
      }
    }
  }

  /**
   * Flash an action button to give visual click feedback (300ms)
   */
  private flashButton(btn: HTMLButtonElement): void {
    btn.classList.add('rv-active');
    setTimeout(() => btn.classList.remove('rv-active'), 300);
  }

  /**
   * Show word input popup (for dictionary/translate)
   */
  private showWordInput(placeholder: string, onSubmit: (value: string) => void): void {
    const existing = document.querySelector('.rv-word-input-overlay');
    if (existing) existing.remove();

    const th = this.theme;
    const overlay = document.createElement('div');
    overlay.className = 'rv-word-input-overlay';
    overlay.style.cssText = ` position: fixed; inset: 0; background: rgba(0,0,0,0.3); z-index: 2147483647; display: flex; align-items: center; justify-content: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; `;

    overlay.innerHTML = ` <div dir="${uiDir()}" style="background:${th.backgroundColor};border-radius:${th.borderRadius}px;padding:24px;min-width:300px;box-shadow:0 8px 32px rgba(0,0,0,0.2);color:${th.textColor};"> <div style="font-size:16px;font-weight:600;margin-bottom:12px;">${placeholder}</div> <input class="rv-word-field" type="text" placeholder="${t('Type here…')}" style=" width:100%;padding:10px 14px;font-size:16px;border:2px solid ${th.borderColor}; border-radius:8px;outline:none;background:${th.backgroundColor};color:${th.textColor}; " /> <div style="display:flex;gap:8px;margin-top:12px;justify-content:flex-end;"> <button class="rv-word-cancel" style="padding:8px 16px;border:1px solid ${th.borderColor};border-radius:8px;background:${th.backgroundColor};cursor:pointer;font-size:14px;color:${th.textColor};">${t('Cancel')}</button> <button class="rv-word-go" style="padding:8px 20px;border:none;border-radius:8px;background:${th.primaryColor};color:${th.textOnPrimary};cursor:pointer;font-size:14px;font-weight:600;">${t('Go')}</button> </div> </div> `;

    document.body.appendChild(overlay);

    const field = overlay.querySelector('.rv-word-field') as HTMLInputElement;
    const goBtn = overlay.querySelector('.rv-word-go') as HTMLButtonElement;
    const cancelBtn = overlay.querySelector('.rv-word-cancel') as HTMLButtonElement;
    field.focus();

    const submit = () => { const v = field.value.trim(); overlay.remove(); onSubmit(v); };
    goBtn.addEventListener('click', submit);
    field.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
    cancelBtn.addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  }

  /**
   * Get a dropdown anchor for a tool (tools call this to position their panels)
   */
  getToolButton(id: ToolId): HTMLButtonElement | null {
    return this.element.querySelector(`[data-tool="${id}"]`) as HTMLButtonElement;
  }

  /**
   * Create/get a dropdown panel anchored to a tool button.
   * Uses position: fixed so it stays anchored to the toolbar regardless of scroll.
   */
  getDropdown(id: ToolId | 'help'): HTMLDivElement {
    let dropdown = this.dropdownElements.get(id as ToolId);
    if (dropdown) {
      // Re-position every time it's requested (button may have moved)
      this.positionDropdown(dropdown, id as ToolId);
      return dropdown;
    }

    dropdown = document.createElement('div');
    dropdown.className = 'rv-tb-dropdown';
    dropdown.setAttribute('data-dropdown', id);

    // Stop clicks inside dropdown from bubbling to the outside-click handler
    dropdown.addEventListener('mousedown', (e) => e.stopPropagation());

    document.body.appendChild(dropdown);
    this.dropdownElements.set(id as ToolId, dropdown);

    this.positionDropdown(dropdown, id as ToolId);
    return dropdown;
  }

  /**
   * Position a dropdown aligned to its tool button
   */
  private positionDropdown(dropdown: HTMLDivElement, id: ToolId): void {
    const btn = id === ('help' as any)
      ? this.element.querySelector('.rv-tb-help') as HTMLButtonElement
      : this.getToolButton(id);
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const dropdownWidth = dropdown.offsetWidth || 200;

    // Center dropdown on the button, but clamp to viewport
    let left = rect.left + rect.width / 2 - dropdownWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));
    dropdown.style.left = `${left}px`;
  }

  /**
   * Update active states on all tool buttons
   */
  updateToolButtonStates(): void {
    const toolBtns = this.element.querySelectorAll('.rv-tb-tool') as NodeListOf<HTMLButtonElement>;
    toolBtns.forEach(btn => {
      const toolId = btn.getAttribute('data-tool') as ToolId;
      if (!toolId) return;
      const tool = this.toolManager.getTool(toolId);
      if (tool) {
        btn.classList.toggle('rv-active', tool.isActive);
      }
    });
  }

  /**
   * Add a badge to a tool button (e.g., font size %)
   */
  setToolBadge(id: ToolId, text: string | null): void {
    const btn = this.getToolButton(id);
    if (!btn) return;

    const existing = btn.querySelector('.rv-tb-badge');
    if (existing) existing.remove();

    if (text) {
      const badge = document.createElement('span');
      badge.className = 'rv-tb-badge';
      badge.textContent = text;
      btn.appendChild(badge);
    }
  }

  /**
   * Set play/pause state
   */
  setState(state: { isPlaying: boolean; progress?: number }): void {
    this.isPlaying = state.isPlaying;
    this.updateSeekPolling();

    const playIcon = this.element.querySelector('.rv-tb-play-icon') as HTMLElement;
    const pauseIcon = this.element.querySelector('.rv-tb-pause-icon') as HTMLElement;
    const playBtn = this.element.querySelector('.rv-tb-play') as HTMLButtonElement;

    if (state.isPlaying) {
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'inline-flex';
      if (playBtn) playBtn.setAttribute('data-tooltip', t('Pause'));
    } else {
      if (playIcon) playIcon.style.display = 'inline-flex';
      if (pauseIcon) pauseIcon.style.display = 'none';
      if (playBtn) playBtn.setAttribute('data-tooltip', t('Play'));
    }
  }

  // ---------------------------------------------------------------------------
  // Summary panel
  // ---------------------------------------------------------------------------

  private summaryPanel: HTMLDivElement | null = null;

  private async showSummaryPanel(): Promise<void> {
    const th = this.theme;
    this.summaryPanel?.remove();
    const panel = document.createElement('div');
    panel.className = 'rv-summary-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', t('Summary'));
    panel.dir = uiDir();
    const edge = this.position === 'top' ? 'top: 56px;' : 'bottom: 56px;';
    panel.style.cssText = `position:fixed;${edge}right:16px;width:min(420px, calc(100vw - 32px));max-height:60vh;overflow:auto; z-index:2147483647;background:${th.backgroundColor};color:${th.textColor};border:1px solid ${th.borderColor}; border-radius:10px;box-shadow:0 8px 24px rgba(0,0,0,.18);padding:14px 16px;font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;`;
    panel.innerHTML = ` <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;"> <strong style="font-size:14px;">${t('Summary')}</strong> <button class="rv-summary-close" aria-label="${t('Close summary')}" style="background:none;border:none;cursor:pointer;color:inherit;font-size:18px;line-height:1;">×</button> </div> <div class="rv-summary-body" aria-live="polite">${t('Summarizing…')}</div>`;
    document.body.appendChild(panel);
    this.summaryPanel = panel;
    const close = () => { panel.remove(); if (this.summaryPanel === panel) this.summaryPanel = null; };
    panel.querySelector('.rv-summary-close')?.addEventListener('click', close);
    panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    const result = await this.callbacks.onSummarize!();
    if (this.summaryPanel !== panel) return;
    const body = panel.querySelector('.rv-summary-body') as HTMLElement;
    if (!result.ok || !result.bullets?.length) {
      body.textContent = result.message || t("Summary isn't available right now.");
      return;
    }
    body.innerHTML = '';
    const ul = document.createElement('ul');
    ul.style.cssText = 'margin:0 0 10px;padding-left:18px;';
    for (const b of result.bullets) {
      const li = document.createElement('li');
      li.textContent = b;
      li.style.marginBottom = '4px';
      ul.appendChild(li);
    }
    body.appendChild(ul);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;align-items:center;justify-content:space-between;gap:8px;';
    const listen = document.createElement('button');
    listen.textContent = t('Listen');
    listen.style.cssText = `background:${th.primaryColor};color:${th.textOnPrimary};border:none;border-radius:6px;padding:6px 12px;cursor:pointer;font-weight:600;`;
    listen.addEventListener('click', () => this.callbacks.onSpeakSummary?.(result.bullets!));
    const note = document.createElement('span');
    note.textContent = t('AI-generated summary');
    note.style.cssText = 'font-size:11px;opacity:.7;';
    row.append(listen, note);
    body.appendChild(row);
    listen.focus();
  }

  // ---------------------------------------------------------------------------
  // Seek bar
  // ---------------------------------------------------------------------------

  private seekTimer: number | null = null;

  private createSeekBar(): HTMLDivElement {
    const track = document.createElement('div');
    track.className = 'rv-tb-seek';
    track.setAttribute('role', 'slider');
    track.setAttribute('aria-label', t('Reading position'));
    track.setAttribute('aria-valuemin', '0');
    track.setAttribute('aria-valuemax', '100');
    track.setAttribute('aria-valuenow', '0');
    track.tabIndex = 0;
    const edge = this.position === 'top' ? 'bottom: -1px;' : 'top: -1px;';
    track.style.cssText = `position:absolute;left:0;right:0;${edge}height:5px;cursor:pointer;background:transparent;`;
    const fill = document.createElement('div');
    fill.className = 'rv-tb-seek-fill';
    fill.style.cssText = `height:100%;width:0%;background:${this.theme.primaryColor};transition:width .3s linear;pointer-events:none;`;
    track.appendChild(fill);
    const seekTo = (pct: number) => {
      this.callbacks.onSeek?.(Math.max(0, Math.min(100, pct)));
      this.refreshSeek();
    };
    track.addEventListener('click', (e) => {
      const r = track.getBoundingClientRect();
      seekTo(((e.clientX - r.left) / r.width) * 100);
    });
    track.addEventListener('keydown', (e) => {
      const now = Number(track.getAttribute('aria-valuenow')) || 0;
      if (e.key === 'ArrowRight') { e.preventDefault(); seekTo(now + 5); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); seekTo(now - 5); }
      if (e.key === 'Home') { e.preventDefault(); seekTo(0); }
    });
    return track;
  }

  private refreshSeek(): void {
    const track = this.element?.querySelector('.rv-tb-seek') as HTMLElement | null;
    if (!track || !this.callbacks.getProgress) return;
    const pct = Math.round(this.callbacks.getProgress() || 0);
    const fill = track.firstElementChild as HTMLElement;
    if (fill) fill.style.width = `${pct}%`;
    track.setAttribute('aria-valuenow', String(pct));
    track.setAttribute('aria-valuetext', `${pct}%`);
  }

  private updateSeekPolling(): void {
    if (!this.callbacks.getProgress) return;
    if (this.isPlaying && this.seekTimer === null) {
      this.seekTimer = window.setInterval(() => this.refreshSeek(), 400);
    } else if (!this.isPlaying && this.seekTimer !== null) {
      window.clearInterval(this.seekTimer);
      this.seekTimer = null;
      this.refreshSeek();
    }
  }

  /**
   * Waiting for the next sentence from the server (play button shows "Loading…")
   */
  setBuffering(buffering: boolean): void {
    const playBtn = this.element.querySelector('.rv-tb-play') as HTMLButtonElement | null;
    if (!playBtn) return;
    playBtn.style.opacity = buffering ? '0.55' : '';
    if (buffering) {
      playBtn.setAttribute('aria-busy', 'true');
      playBtn.setAttribute('data-tooltip', t('Loading…'));
    } else {
      playBtn.removeAttribute('aria-busy');
      playBtn.setAttribute('data-tooltip', this.isPlaying ? t('Pause') : t('Play'));
    }
  }

  /**
   * Set the voice in the dropdown
   */
  setVoice(voiceId: string): void {
    const select = this.element.querySelector('.rv-tb-voice') as HTMLSelectElement;
    if (select) select.value = voiceId;
  }

  /**
   * Push page content down/up to make room for toolbar
   */
  pushPageContent(): void {
    if (this.position === 'top') {
      document.body.style.marginTop = '48px';
    } else {
      document.body.style.marginBottom = '48px';
    }
  }

  /**
   * Restore page content position
   */
  restorePageContent(): void {
    if (this.position === 'top') {
      document.body.style.marginTop = '';
    } else {
      document.body.style.marginBottom = '';
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard shortcuts
  // ---------------------------------------------------------------------------

  private handleShortcutKey(e: KeyboardEvent): void {
    // Settings marks the document while it records a new binding
    if (e.repeat || document.documentElement.hasAttribute('data-rv-capturing-shortcut')) return;
    const settings = this.toolManager.getSettings();
    if (settings.shortcutsEnabled === false) return;
    if (isEditableTarget(e.target)) return;
    const binding = bindingFromEvent(e);
    if (!binding) return;
    const bindings = effectiveShortcuts(settings.shortcuts as any);
    const action = (Object.keys(bindings) as ShortcutAction[]).find(a => bindings[a] === binding);
    if (!action) return;
    e.preventDefault();
    e.stopPropagation();
    this.runShortcut(action);
  }

  /** Perform a shortcut by pressing the same control a mouse user would */
  runShortcut(action: ShortcutAction): void {
    const q = (sel: string) => this.element.querySelector(sel) as HTMLButtonElement | null;
    const ensureVisible = () => {
      if (this.isVisible()) return;
      const toggle = document.querySelector('.rv-toggle-btn') as HTMLButtonElement | null;
      if (toggle) toggle.click(); else this.show();
    };
    switch (action) {
      case 'playPause': ensureVisible(); q('.rv-tb-play')?.click(); break;
      case 'stop': q('.rv-tb-stop')?.click(); break;
      case 'faster': q('.rv-tb-speed-up')?.click(); break;
      case 'slower': q('.rv-tb-speed-down')?.click(); break;
      case 'next': this.callbacks.onSkip?.(1); break;
      case 'previous': this.callbacks.onSkip?.(-1); break;
      case 'toolbar': {
        const toggle = document.querySelector('.rv-toggle-btn') as HTMLButtonElement | null;
        if (toggle) toggle.click(); else (this.isVisible() ? this.hide() : this.show());
        if (this.isVisible()) q('.rv-tb-play')?.focus();
        break;
      }
      case 'settings': ensureVisible(); q('.rv-tb-tool[data-tool="settings"]')?.click(); break;
      case 'translate': ensureVisible(); q('.rv-tb-tool[data-tool="translate"]')?.click(); break;
      case 'dictionary': ensureVisible(); q('.rv-tb-tool[data-tool="dictionary"]')?.click(); break;
      case 'summary': ensureVisible(); q('.rv-tb-tool[data-tool="summary"]')?.click(); break;
      case 'download': ensureVisible(); q('.rv-tb-tool[data-tool="download"]')?.click(); break;
      case 'clickListen': ensureVisible(); q('.rv-tb-tool[data-tool="clickListen"]')?.click(); break;
      case 'formReader': ensureVisible(); q('.rv-tb-tool[data-tool="formReader"]')?.click(); break;
      case 'help': ensureVisible(); q('.rv-tb-help')?.click(); break;
    }
  }

  /**
   * Show toolbar
   */
  private shownOnce = false;
  show(): void {
    this.element.style.display = 'flex';
    if (!this.shownOnce) {
      this.shownOnce = true;
      this.callbacks.onFirstShow?.();
      document.dispatchEvent(new CustomEvent('rv-engaged'));
    }
    this.pushPageContent();
  }

  /**
   * Hide toolbar
   */
  hide(): void {
    this.element.style.display = 'none';
    if (this.captionEl) this.captionEl.style.display = 'none';
    this.restorePageContent();
    // Close everything the toolbar opened, overlays included (magnifier, mask,
    // click-and-listen, form reader, simplify): with the toolbar hidden there's
    // no visible control left to turn them off
    this.toolManager.deactivateAll();
    this.updateToolButtonStates();
    if (this.helpOpen) {
      this.dropdownElements.get('help' as any)?.classList.remove('rv-open');
      this.helpOpen = false;
      this.element.querySelector('.rv-tb-help')?.classList.remove('rv-active');
    }
    document
      .querySelectorAll('.rv-summary-panel, .rv-word-input-overlay, .rv-dictionary-popup, .rv-translation-popup')
      .forEach((el) => el.remove());
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.element.style.display !== 'none';
  }

  /**
   * Get the toolbar element
   */
  getElement(): HTMLDivElement {
    return this.element;
  }

  /**
   * Get toolbar position
   */
  getPosition(): 'top' | 'bottom' {
    return this.position;
  }

  /**
   * Toggle help dropdown
   */
  private helpOpen = false;
  private async toggleHelpDropdown(): Promise<void> {
    const dropdown = this.getDropdown('help' as any);

    if (this.helpOpen) {
      dropdown.classList.remove('rv-open');
      this.helpOpen = false;
      this.element.querySelector('.rv-tb-help')?.classList.remove('rv-active');
      return;
    }

    // Close other dropdowns first
    this.toolManager.closeAllTools();
    this.updateToolButtonStates();

    dropdown.style.minWidth = '300px';
    try {
      const { buildToolbarHelpHTML } = await loadExtras();
      dropdown.innerHTML = buildToolbarHelpHTML({
        theme: this.theme,
        icons: ICONS,
        isToolEnabled: (id) => this.isToolEnabled(id),
        selectionTranslation: !!this.features?.selectionTranslation,
        fullPageTranslation: !!(this.features?.fullPageTranslation && this.callbacks.onLanguageChange),
        standardVoices: this.features?.voiceTier === 'standard',
      });
    } catch {
      dropdown.innerHTML = `<a href="https://readingvox.com/support" target="_blank" rel="noopener" style="color:${this.theme.primaryColor};">${t('ReadingVox help')}</a>`;
    }
    dropdown.classList.add('rv-open');
    this.helpOpen = true;
    this.element.querySelector('.rv-tb-help')?.classList.add('rv-active');
  }

  /**
   * Compute a muted color that meets WCAG AA 4.5:1 contrast against bgColor.
   * Starts at midpoint between text and bg, shifts toward text until ratio is met.
   */
  private computeWcagMuted(textColor: string, bgColor: string): string {
    const textRgb = hexToRgb(textColor);
    const bgRgb = hexToRgb(bgColor);

    // Lerp from bg toward text; t=0 is bg, t=1 is text
    const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
    const toHex = (r: number, g: number, b: number) =>
      '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');

    // Start at midpoint (0.5) and walk toward text color
    for (let t = 0.5; t <= 1.0; t += 0.05) {
      const r = lerp(bgRgb.r, textRgb.r, t);
      const g = lerp(bgRgb.g, textRgb.g, t);
      const b = lerp(bgRgb.b, textRgb.b, t);
      const candidate = toHex(r, g, b);
      if (contrastRatio(candidate, bgColor) >= 7.0) {
        return candidate;
      }
    }
    // Fallback to text color if nothing else works
    return textColor;
  }

  /**
   * Refresh toolbar theme (called when user changes toolbar theme setting)
   */
  refreshTheme(toolbarTheme: ToolbarTheme): void {
    // Re-resolve theme: if 'auto', use original; otherwise force light/dark
    const mode = toolbarTheme === 'auto' ? this.baseThemeConfig.mode : toolbarTheme;
    this.theme = resolveTheme({
      mode,
      primaryColor: this.baseThemeConfig.primaryColor,
      secondaryColor: this.baseThemeConfig.secondaryColor,
      borderRadius: this.baseThemeConfig.borderRadius,
    });

    // Update CSS variables (injectStyles reuses existing style element)
    this.injectStyles();

    // Update bar inline styles
    const th = this.theme;
    this.element.style.background = th.backgroundColor;
    this.element.style.color = th.textColor;
    this.element.style.borderColor = th.borderColor;

    // Rebuild bar content to pick up new colors in inline styles
    this.element.innerHTML = this.buildHTML();
    this.attachEventListeners(this.element);
    this.applyFeatureSlots();
    this.updateToolButtonStates();

    // Restore play state
    if (this.isPlaying) {
      this.setState({ isPlaying: true });
    }

    // Update dropdown backgrounds
    this.dropdownElements.forEach(dd => {
      dd.style.background = th.backgroundColor;
      dd.style.color = th.textColor;
      dd.style.borderColor = th.borderColor;
    });
  }

  /**
   * Show a "TRIAL" badge pill next to the logo
   */
  showTrialBadge(): void {
    const logo = this.element.querySelector('.rv-tb-logo');
    if (!logo || logo.querySelector('.rv-trial-pill')) return;

    const pill = document.createElement('span');
    pill.className = 'rv-trial-pill';
    pill.textContent = 'TRIAL';
    pill.style.cssText = ` display: inline-flex; align-items: center; padding: 2px 8px; margin-left: 6px; background: linear-gradient(135deg, #f59e0b, #d97706); color: #fff; font-size: 10px; font-weight: 700; border-radius: 10px; letter-spacing: 0.5px; text-transform: uppercase; line-height: 1; `;
    logo.appendChild(pill);
  }

  /**
   * Destroy toolbar
   */
  destroy(): void {
    this.summaryPanel?.remove();
    if (this.seekTimer !== null) window.clearInterval(this.seekTimer);
    if (this.boundKeyDown) document.removeEventListener('keydown', this.boundKeyDown, true);
    this.ariaObserver?.disconnect();
    this.ariaObserver = null;
    this.restorePageContent();
    this.dropdownElements.forEach(dd => dd.remove());
    this.dropdownElements.clear();
    document.removeEventListener('mousedown', this.boundDocMouseDown);
    this.captionEl?.remove();
    this.captionEl = null;
    this.element.remove();
  }
}
